/** Display formatting. Ported from the prototype; money inputs are decimal strings. */
import Decimal from 'decimal.js';

interface MoneyOpts {
  /** Show a leading + for positive values. */
  sign?: boolean;
  /** Fraction digits (default 2). */
  dp?: number;
  /** Compact large numbers (e.g. 1.2M, 24k). */
  compact?: boolean;
}

export function fmtMoney(value: string | number | null | undefined, opts: MoneyOpts = {}): string {
  const { sign = false, dp = 2, compact = false } = opts;
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return '—';
  const abs = Math.abs(n);

  let body: string;
  if (compact && abs >= 1000) {
    body =
      abs >= 1e6 ? `${(abs / 1e6).toFixed(2)}M` : `${(abs / 1e3).toFixed(abs >= 10000 ? 0 : 1)}k`;
  } else {
    body = abs.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  }
  const prefix = n < 0 ? '−' : sign ? '+' : '';
  return `${prefix}$${body}`;
}

/** Signed money: always shows + or −. */
export function fmtSignedMoney(value: string | number, dp = 2): string {
  return fmtMoney(value, { sign: true, dp });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function fmtWeekday(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
}

/** Whole days from a to b (b − a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** Add n days to a YYYY-MM-DD string. */
export function addDays(iso: string, n: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Today as YYYY-MM-DD (local). */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Exact decimal difference b − a as a 2dp string. */
export function diffMoney(a: string, b: string): string {
  return new Decimal(b).minus(a).toFixed(2);
}
